import DetailsLinkRenderer from '../renderers/DetailsLinkRenderer'
import {i18n} from "../components/app/AppSetup"
import FormatService from "./FormatService"
import TimeUtil from "./timeUtil"
import {markRaw} from "vue"

export default {
    /**
     * Gets the default gridOptions for a typical infinite scrolling table, allowing property overrides to the defaults.
     * Does not return columnDefs, the caller should set these after calling this method.
     * @param context - calling context, e.g. 'this'
     * @param options - ag-grid options object, any properties specified here will override or add to the default properties
     * @returns {object} - ag-grid gridOptions, excluding columnDefs
     */
    getInfiniteScrollGridOptions(context = {}, options = {}, defaultColDefs = {}) {
        let defaultOptions = {
            rowModelType: 'infinite',
            rowSelection: {mode: 'multiRow', checkboxes: false, headerCheckbox: false, enableClickSelection: true, enableSelectionWithoutKeys: true},
            suppressCellFocus: true,
            colResizeDefault: 'shift',
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            enableCellTextSelection: true,
            context: markRaw({
                parent: context,
                i18n: i18n,
                router: context.$router
            }),
            defaultColDef: Object.assign({
                sortingOrder: ['asc', 'desc'],
                sortable: true,
                resizable: true
            }, defaultColDefs)
        }
        return Object.assign(defaultOptions, options)
    },

    /**
     * Gets the default gridOptions for a typical full length table (autoHeight), without infinite scrolling, used on detail pages,
     * allowing property overrides to the defaults.
     * Does not return columnDefs, the caller should set these after calling this method.
     * @param context - calling context, e.g. 'this'
     * @param options - ag-grid options object, any properties specified here will override or add to the default properties
     * @returns {object} - ag-grid gridOptions, excluding columnDefs
     */
    getAutoHeightGridOptions(context = {}, options = {}, defaultColDefs = {}) {
        let defaultOptions = {
            animateRows: false,
            rowModelType: 'clientSide',
            rowSelection: {mode: 'singleRow', checkboxes: false, headerCheckbox: false, enableClickSelection: false, enableSelectionWithoutKeys: true},
            suppressCellFocus: true,
            colResizeDefault: 'shift',
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            enableCellTextSelection: true,
            context: markRaw({
                parent: context,
                i18n: i18n,
                router: context.$router
            }),
            defaultColDef: Object.assign({
                sortingOrder: ['asc', 'desc'],
                sortable: true,
                resizable: true
            }, defaultColDefs)
        }
        return Object.assign(defaultOptions, options)
    },

    /**
     * Gets the default gridOptions for a client side table with non-selectable rows, without infinite scrolling or autoHeight,
     * sometimes used for dashlets, allowing property overrides to the defaults.
     * Does not return columnDefs, the caller should set these after calling this method.
     * @param context - calling context, e.g. 'this'
     * @param options - ag-grid options object, any properties specified here will override or add to the default properties
     * @param defaultColDefs - replace all columns definitions with the passed in ones
     * @param countColDef - the count column definition to override the default, this can be an array of columns to replace the count column
     * @returns {object} - ag-grid gridOptions, excluding columnDefs
     */
    getDefaultDashletTableOptions(context = {}, options = {}, defaultColDefs = {}, countColDef) {
        let defaultOptions = {
            rowModelType: 'clientSide',
            rowSelection: {checkboxes: false, enableClickSelection: false},
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            colResizeDefault: 'shift',
            enableCellTextSelection: true,
            context: markRaw({
                tableUserPrefsKey: context.options.value+"-table",
                parent: context,
                i18n: i18n,
                router: context.$router
            }),
            defaultColDef: Object.assign({
                sortingOrder: ['asc', 'desc'],
                sortable: true,
                resizable: true
            }, defaultColDefs)
        }
        if (context.options.tableCols)
        {
            defaultOptions.columnDefs = context.options.tableCols
        }
        else
        {
            defaultOptions.columnDefs = [
                {headerName: i18n.global.t('metricsDB.widgets.noise.baseStationId'), field: 'baseStationId', width: 65, minWidth: 65,
                    cellRenderer: DetailsLinkRenderer, cellRendererParams: {routeName: 'towerDetails'}},
                {headerName: i18n.global.t('metricsDB.widgets.baseStationCounts.name'), field: 'name', width: 60, minWidth: 60,
                    cellRenderer: DetailsLinkRenderer, cellRendererParams: {routeName: 'towerDetails', idField: 'baseStationId'}},
                {headerName: i18n.global.t('metricsDB.widgets.baseStationCounts.location'), field: 'address', width: 70, minWidth: 70 },
            ]
            // Substitute the passed in counter columns for the default
            if (context.options.counterCols)
            {
                Array.prototype.push.apply (defaultOptions.columnDefs, context.options.counterCols)
            }
            else
            {
                defaultOptions.columnDefs.push ({headerName: i18n.global.t('metricsDB.widgets.baseStationCounts.count'), field: 'value', width: 60, minWidth: 60, valueFormatter: FormatService.numberFormatter})
            }
            defaultOptions.columnDefs.push ({headerName: i18n.global.t('metricsDB.widgets.baseStationCounts.received'), field: 'received.displayString', width: 100, valueFormatter: TimeUtil.nmFormatDateWithTZ})
        }
        return Object.assign(defaultOptions, options)
    },
    /**
     * Determine if all rows shown in a table are selected
     * @param tableApi - ag grid table api object
     * @return {boolean} - true if all rows are selected, false otherwise
     */
    allRowsSelected(tableApi)
    {
        // How many rows are there displayed
        let displayedRows = tableApi.getDisplayedRowCount()

        // How many rows are selected
        let selectedRows = tableApi.getSelectedRows()?.length || 0

        let pageSize = tableApi.paginationGetPageSize() || displayedRows

        // AG Grid adds overflow rows while the last row index is unknown/while the user has not scrolled to the last row (typical in infinite scrolling)
        // how many depends on the cacheOverflowSize property
        // Therefore to calculate allRowsSelected, we subtract cacheOverflowSize only if !isLastRowIndexKnown()
        // Once the datasource provides `lastRow` to successCallback, the model knows the last row and the overflow row disappears, so we do not subtract it then

        // If the num of displayed rows is less than or equal to the page size, the overflow row is not a factor.
        // Only subtract overflow size while last row is unknown (overflow row present).
        if (displayedRows > pageSize && !tableApi.isLastRowIndexKnown())
        {
            let overflowSize = tableApi.getGridOption?.('cacheOverflowSize') || 1
            return selectedRows === (displayedRows - overflowSize)
        } else if (displayedRows !== 0) {
            return selectedRows === displayedRows
        } else {
            return false
        }
    },
}
